import { useCallback, useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";

// Tracks device geolocation and updates in real time via watchPosition.
// Returns { position, error, status, requestPermission } where position
// contains { lat, lng, accuracy, heading, speed, timestamp }.
export default function useGeolocation({
  enabled = true,
  enableHighAccuracy = true,
  maximumAge = 5000,
  timeout = 20000,
} = {}) {
  const [position, setPosition] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("idle");
  // Bumping this triggers the effect to re-attempt permission + watch.
  const [retryTick, setRetryTick] = useState(0);

  const requestPermission = useCallback(async () => {
    if (Capacitor.isNativePlatform()) {
      try {
        const req = await Geolocation.requestPermissions({ permissions: ["location"] });
        if (req.location === "granted" || req.coarseLocation === "granted") {
          setRetryTick((n) => n + 1);
          return true;
        }
      } catch (_) {}
      return false;
    }
    setRetryTick((n) => n + 1);
    return true;
  }, []);

  useEffect(() => {
    if (!enabled) {
      setStatus("idle");
      return;
    }

    let cancelled = false;
    let watchId = null; // numeric (web) or string (native)
    const isNative = Capacitor.isNativePlatform();

    setStatus("requesting");

    const handlePos = (pos) => {
      if (cancelled || !pos || !pos.coords) return;
      setStatus("available");
      setError(null);
      setPosition({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        altitude: pos.coords.altitude,
        heading: pos.coords.heading,
        speed: pos.coords.speed,
        timestamp: pos.timestamp,
      });
    };

    const handleErr = (err) => {
      if (cancelled) return;
      setError(err);
      setStatus("denied");
    };

    if (isNative) {
      (async () => {
        try {
          const perm = await Geolocation.checkPermissions();
          if (perm.location !== "granted" && perm.coarseLocation !== "granted") {
            const req = await Geolocation.requestPermissions({ permissions: ["location"] });
            if (req.location !== "granted" && req.coarseLocation !== "granted") {
              handleErr(new Error("Location permission denied"));
              return;
            }
          }
          if (cancelled) return;
          watchId = await Geolocation.watchPosition(
            { enableHighAccuracy, maximumAge, timeout },
            (pos, err) => {
              if (err) return handleErr(err);
              handlePos(pos);
            }
          );
        } catch (e) {
          handleErr(e);
        }
      })();

      return () => {
        cancelled = true;
        if (watchId) {
          Geolocation.clearWatch({ id: watchId }).catch(() => {});
        }
      };
    }

    if (!navigator.geolocation) {
      setError(new Error("Geolocation API not supported."));
      setStatus("unsupported");
      return;
    }

    watchId = navigator.geolocation.watchPosition(
      (pos) => handlePos(pos),
      (err) => handleErr(err),
      {
        enableHighAccuracy,
        maximumAge,
        timeout,
      }
    );

    return () => {
      cancelled = true;
      navigator.geolocation.clearWatch(watchId);
    };
  }, [enabled, enableHighAccuracy, maximumAge, timeout, retryTick]);

  return { position, error, status, requestPermission };
}
